import { store } from '@/common/store';
import { usePropertyStore } from '@/common/stores/property';
import { useAuthStore } from '@/common/stores/auth';
import { useLoginRoute } from '@/login/components/loginForm/loginForm.uses';
import { storeToRefs } from 'pinia';
import { useViewManagerStore } from '@/common/stores/view-manager';
import { RouteLocationNormalized, NavigationGuardNext } from 'vue-router';
import { queryClient } from '@/common/lib/queryClient';
import { getGetV7PropertyQueryKey } from '@/api-client/common/queries/mode-controller/mode-controller';
import { prefetchProperty, prefetchDashboardUserEnv } from '@/common/lib/prefetch';
import { useUserEnvStore } from '@/common/stores/user-env';
import { useDatabaseMetaStore } from '@/database/stores/database-meta';
import { useDatabasePaConditionStore } from '@/database/stores/pa-condition';
import { useBaseMenuStore } from '../stores/base-menu';
import { initializeApp, initializeAppForDevTesting } from './router.uses';
import { useLinkManagerStore } from '../stores/link-manager';
import { PATH, requireAuth, setDayjsTimezone } from './authGuard';
import { useDatabaseOverviewStore } from '@/database/stores/database-overview';
import { useMyInfoStore } from '@/config/stores/my-info';
import { isDashboardRoute, normalizeQueryStringParam } from './router.utils';
import { useDashboardUserEnvStore } from '@/dashboard/stores/dashboard-user-env';
/**
 * 로그아웃 시 챗봇의 활성 대화 ID localStorage 정리 — 다음 로그인은 새 사용자 컨텍스트로 시작.
 * useChatConversationsStore 의 LS_ACTIVE_KEY 와 일치.
 */
const clearChatbotActiveConversation = (): void => {
  try {
    localStorage.removeItem('chatbotActiveConversationId');
  } catch {
    /* ignore */
  }
};

// const showErrorMsg = (msg: string) => {
//   EvMessage({
//     type: 'error',
//     message: msg,
//   });
// };

const ControlPageLoading = {
  active: () => {
    const { isLoadingRouterForEach } = storeToRefs(useViewManagerStore());
    isLoadingRouterForEach.value = true;
  },
  inactive: () => {
    const { isLoadingRouterForEach } = storeToRefs(useViewManagerStore());
    isLoadingRouterForEach.value = false;
  },
};

const validateToken = async () => {
  const myInfoStore = useMyInfoStore();
  // 토큰 검증 로직
  // -> 화면 api요청 시 사용하는 토큰 기반으로 검증
  try {
    let { userId } = myInfoStore.myInfo;
    if (userId === -1) {
      ControlPageLoading.active();
      await myInfoStore.dispatchMyInfo();
      userId = myInfoStore.myInfo.userId;
    }
    return !!userId;
  } catch {
    return false;
  }
};

const clearModule = () => {
  useAuthStore().clear();
  useBaseMenuStore().resetMenuInfo();
  useUserEnvStore().initUserEnvMap();

  useMyInfoStore().initAccountInfo();

  useDashboardUserEnvStore().initUserEnvMap();
  useDatabaseMetaStore().initAll();
  useDatabasePaConditionStore().initAllSelectedInfo();
  useDatabaseOverviewStore().initLastSelected();
};

const skipLogin = async () => {
  const authStore = useAuthStore();
  await authStore.setInitialInfo();
  const hasAuth = authStore.getToken('accessToken');

  if (hasAuth) {
    // 사용자 정보 검증
    const isValid = await validateToken();
    if (!isValid) {
      clearModule();
      return undefined;
    }
    const startScreenId = await useLoginRoute().getStartDashboardId();
    if (startScreenId > 0) {
      return {
        name: 'dashboard_Dashboard View',
        params: {
          id: startScreenId,
        },
      };
    }
    return PATH.HOME;
  }

  clearModule();
  return undefined;
};

const startLoading = () => {
  const { isPageLoading } = storeToRefs(useViewManagerStore());
  isPageLoading.value = true;

  store.commit('setIsLoading', true);
};

const logoutApp = async (to: RouteLocationNormalized) => {
  const { propertyData } = storeToRefs(usePropertyStore());
  await useAuthStore().setTokenLogout();
  // 챗봇 thread 토큰 제거 — 다음 로그인 시 새 thread 시작.
  clearChatbotActiveConversation();
  clearModule();

  const redirectQuery = to.redirectedFrom?.fullPath
    ? `?redirectPath=${encodeURIComponent(to.redirectedFrom.fullPath)}`
    : '';

  if (propertyData.value?.sso?.ssoMode) {
    const locationPath =
      (to.query.logoutTriggered
        ? propertyData.value.sso.redirectionLogoutUrl
        : propertyData.value.sso.ssoPath) || '/';
    window.location.href = `${locationPath}${redirectQuery}`;
    return false;
  }

  window.location.href = `/exemone.html${redirectQuery}`;
  return false;
};

const SSO_AUTH_LOCKED_ERROR_CODE = 'E011';

const ssoCheck = (to) => {
  if (!to.query.id || to.query.error || to.query.errorCode) {
    let errorParams = '';
    if (to.query.error) {
      errorParams += `error=${to.query.error}`;
    }

    if (to.query.errorCode) {
      errorParams += `${errorParams ? '&&' : ''}errorCode=${to.query.errorCode}`;
    }

    const redirectPath = normalizeQueryStringParam(to.query.redirectPath);
    if (
      redirectPath &&
      normalizeQueryStringParam(to.query.errorCode) === SSO_AUTH_LOCKED_ERROR_CODE
    ) {
      errorParams += `${errorParams ? '&&' : ''}redirectPath=${redirectPath}`;
    }

    return `${PATH.AUTH_FAIL}?${errorParams}`;
  }
  return undefined;
};

// 연계 뷰 확인 후 화면 넘김
const routerToLinkedView = (to: RouteLocationNormalized, next: NavigationGuardNext) => {
  const linkManagerStore = useLinkManagerStore();
  const { initLinkManagerData, getLinkManagerRoute } = linkManagerStore;

  initLinkManagerData(to);

  const route = getLinkManagerRoute();

  // 화면 로드 전에 즉시 브라우저 URL에서 query params 제거
  const cleanPath = typeof route === 'string' ? route : route.path || to.path;
  if (cleanPath && window.history.replaceState) {
    window.history.replaceState(null, '', cleanPath);
  }

  return next(route);
};

const initializeRouterGuard = async (
  to: RouteLocationNormalized,
  from: RouteLocationNormalized,
  next: NavigationGuardNext,
) => {
  if (to.path === PATH.LOGOUT || to.path === PATH.PUBLIC_POPUP) {
    return next();
  }

  // dashboard id가 변경될 때만 dashboard user env prefetch
  if (
    isDashboardRoute(to) &&
    to.params?.id != null &&
    from.name === to.name &&
    from.params?.id !== to.params?.id
  ) {
    try {
      await prefetchDashboardUserEnv(+to.params?.id);
    } catch (e: unknown) {
      console.error('Dashboard user env prefetch 실패:', e);
    }
  }

  const { isSetProperty } = storeToRefs(usePropertyStore());

  if (isSetProperty.value) {
    return next();
  }

  try {
    if (to.query.linkToken) {
      return routerToLinkedView(to, next);
    }

    const propertyData = queryClient.getQueryData(getGetV7PropertyQueryKey());
    if (propertyData === undefined) {
      await prefetchProperty();
    }

    await initializeApp();

    // maxgauge 모드 원활한 테스트를 위한 코드
    initializeAppForDevTesting();

    return next();
  } catch (e: any) {
    console.error('Property API 호출 실패:', e?.message || e);

    if (to.path === PATH.ROOT || to.path === '/exemone.html') {
      return next();
    }

    return next('/logout');
  }
};

export {
  PATH,
  ControlPageLoading,
  clearModule,
  skipLogin,
  validateToken,
  requireAuth,
  startLoading,
  setDayjsTimezone,
  logoutApp,
  ssoCheck,
  initializeRouterGuard,
};
